import type { AppContext } from "../app/app";
import { el } from "./dom";

// A one-time, at-point-of-use notice before any judgement-partner feature first
// transmits the user's own notes to the model. STORAGE stays local; PROCESSING
// does not. We ask once, then remember the acknowledgement.
const ACK_KEY = "processing_ack";

export async function processingConsented(
  repo: AppContext["repo"],
): Promise<boolean> {
  return (await repo.getConfig(ACK_KEY)) === "1";
}

// Persist the acknowledgement so the notice does not reappear.
export async function acknowledgeProcessing(repo: AppContext["repo"]): Promise<void> {
  await repo.setConfig(ACK_KEY, "1");
}

// Resolve true once the user may proceed: immediately if already acknowledged,
// otherwise after they acknowledge the notice. Resolves false if they cancel.
export function requireProcessingConsent(ctx: AppContext): Promise<boolean> {
  return processingConsented(ctx.repo).then((ok) => {
    if (ok) return true;
    return new Promise<boolean>((resolve) => {
      const overlay = el("div", { class: "consent" });

      const close = (granted: boolean) => {
        overlay.remove();
        resolve(granted);
      };

      const acknowledge = el("button", {
        class: "btn btn--primary",
        text: "Acknowledge and continue",
        on: {
          click: async () => {
            await acknowledgeProcessing(ctx.repo);
            close(true);
          },
        },
      });
      const cancel = el("button", {
        class: "btn btn--ghost",
        text: "Not now",
        on: { click: () => close(false) },
      });

      overlay.append(
        el(
          "div",
          { class: "consent__card", attrs: { role: "dialog", "aria-modal": "true" } },
          el("span", { class: "label", text: "AI processing" }),
          el("h2", { class: "consent__title", text: "Before you use this feature." }),
          el("p", {
            class: "lede",
            text:
              "Sharpen, Surface and Assemble work by sending the notes you have " +
              "written, including the names of, and your opinions about, other " +
              "people, to our AI provider (Anthropic) to produce the analysis. " +
              "This happens each time you use these features. Your notes are not " +
              "stored on STRAD's servers; they stay in this browser. On the STRAD " +
              "shared key, requests pass through STRAD's proxy, which stores " +
              "nothing; you can instead use your own API key in Settings. Do not " +
              "enter information you are not permitted to share, and do not record " +
              "sensitive personal data (health, politics, religion, sexuality, and " +
              "the like).",
          }),
          el("div", { class: "consent__actions" }, acknowledge, cancel),
        ),
      );
      document.body.append(overlay);
    });
  });
}
